import { useState } from 'react'
import { Screen, SectionHeader, ViewAllLink } from '@/components/layout'
import { Banner, Button, TextField } from '@/components/primitives'
import { ConfirmDialog } from '@/components/dialog'
import { DetailRow, DetailRows, SummaryCard } from '@/components/cards'
import { CoverageBar } from '@/components/charts/Bars'
import { List, StatementRow, TransactionRow } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonCard, SkeletonList } from '@/components/states'
import { ApiFailure, api } from '@/lib/api'
import { t } from '@/lib/copy'
import { accountTypeName } from '@/lib/display'
import { coverageSegments } from '@/lib/coverage'
import { formatBasisPoints, formatDate, formatMoney } from '@/lib/format'
import { useNavigate, useRoute } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

/** One account: balance, statement coverage, and its transactions. */
export function AccountDetailScreen() {
  const route = useRoute()
  const navigate = useNavigate()
  const id = route.params.id

  const account = useAsync(() => api.accounts.get(id), [id])
  const statements = useAsync(() => api.accounts.statements(id), [id])
  const transactions = useAsync(
    (signal) => api.transactions.list({ accountId: id, limit: 20 }, signal),
    [id],
  )

  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState('')
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ApiFailure | null>(null)

  const data = account.state.data
  const rows = transactions.state.data?.items ?? []

  const screenState = error
    ? 'error'
    : account.state.status === 'loading' && !data
      ? 'loading'
      : account.state.status === 'error'
        ? 'error'
        : rows.length === 0 && (statements.state.data?.length ?? 0) === 0
          ? 'empty'
          : 'ready'

  const rename = async () => {
    if (!data) return
    setBusy(true)
    setError(null)
    try {
      const updated = await api.accounts.update(id, { name: name.trim() || data.name })
      account.setData(updated)
      setRenaming(false)
    } catch (e) {
      setError(e instanceof ApiFailure ? e : null)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      await api.accounts.remove(id)
      navigate('accounts', {}, { replace: true })
    } catch (e) {
      setError(e instanceof ApiFailure ? e : null)
      setConfirmingDelete(false)
    } finally {
      setBusy(false)
    }
  }

  if (screenState === 'loading') {
    return (
      <Screen id="accountDetail" state={screenState} back={{ screen: 'accounts' }}>
        <LoadingRegion>
          <SkeletonCard />
          <SkeletonList rows={6} />
        </LoadingRegion>
      </Screen>
    )
  }

  if (!data) {
    return (
      <Screen id="accountDetail" state="error" back={{ screen: 'accounts' }}>
        <ErrorState
          titleKey="accountDetail.error.title"
          error={account.state.status === 'error' ? account.state.error : undefined}
          onRetry={account.reload}
        />
      </Screen>
    )
  }

  const gaps = data.coverage?.gaps ?? []

  return (
    <Screen
      id="accountDetail"
      state={screenState}
      title={data.name}
      subtitle={accountTypeName(data.type)}
      back={{ screen: 'accounts' }}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              setName(data.name)
              setRenaming(true)
            }}
          >
            {t('accountDetail.cta.rename')}
          </Button>
          <Button variant="secondary" onClick={() => navigate('upload')}>
            {t('accountDetail.cta.addStatement')}
          </Button>
          <Button variant="destructive" onClick={() => setConfirmingDelete(true)}>
            {t('accountDetail.cta.delete')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {error ? <ErrorState titleKey="accountDetail.error.title" error={error} onRetry={() => setError(null)} /> : null}

        {gaps.map((gap) => (
          <Banner key={`${gap.from}:${gap.to}`} tone="caution">
            {t('accountDetail.gap', { from: formatDate(gap.from), to: formatDate(gap.to) })}
          </Banner>
        ))}

        {renaming ? (
          <div className="card stack">
            <TextField
              label={t('accountDetail.cta.rename')}
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
            <div className="row">
              <Button variant="primary" onClick={() => void rename()} disabled={busy}>
                {t('cta.save')}
              </Button>
              <Button variant="quiet" onClick={() => setRenaming(false)} disabled={busy}>
                {t('cta.cancel')}
              </Button>
            </div>
          </div>
        ) : null}

        <SummaryCard>
          <DetailRows>
            <DetailRow label={t('accountDetail.balance')}>{formatMoney(data.currentBalance)}</DetailRow>
            {data.availableBalance ? (
              <DetailRow label={t('accountDetail.available')}>{formatMoney(data.availableBalance)}</DetailRow>
            ) : null}
            {data.creditLimit ? (
              <DetailRow label={t('accountDetail.limit')}>{formatMoney(data.creditLimit)}</DetailRow>
            ) : null}
            {data.aprBasisPoints !== null && data.aprBasisPoints !== undefined ? (
              <DetailRow label={t('accountDetail.apr')}>{formatBasisPoints(data.aprBasisPoints)}</DetailRow>
            ) : null}
          </DetailRows>

          {data.coverage?.from && data.coverage.to ? (
            <div className="stack-sm" style={{ marginTop: 'var(--space-md)' }}>
              <CoverageBar
                segments={coverageSegments(data.coverage)}
                label={t('accounts.row.coverage', {
                  from: formatDate(data.coverage.from),
                  to: formatDate(data.coverage.to),
                })}
              />
              <span className="caption">
                {t('accounts.row.coverage', {
                  from: formatDate(data.coverage.from),
                  to: formatDate(data.coverage.to),
                })}
              </span>
            </div>
          ) : null}

          {data.isDebt ? (
            <Button variant="secondary" onClick={() => navigate('debt')} style={{ marginTop: 'var(--space-md)' }}>
              {t('debt.title')}
            </Button>
          ) : null}
        </SummaryCard>

        <section>
          <SectionHeader title={t('accountDetail.section.statements')} />
          {statements.state.status === 'loading' && !statements.state.data ? (
            <SkeletonList rows={3} />
          ) : (statements.state.data?.length ?? 0) === 0 ? (
            <EmptyState
              titleKey="accounts.row.noCoverage"
              actionLabel={t('accountDetail.cta.addStatement')}
              onAction={() => navigate('upload')}
            />
          ) : (
            <List>
              {(statements.state.data ?? []).map((statement) => (
                <StatementRow key={statement.id} statement={statement} />
              ))}
            </List>
          )}
        </section>

        <section>
          <SectionHeader
            title={t('accountDetail.section.transactions')}
            action={<ViewAllLink screen="transactions" />}
          />
          {screenState === 'empty' ? (
            <EmptyState titleKey="accountDetail.empty.title" />
          ) : transactions.state.status === 'loading' && rows.length === 0 ? (
            <SkeletonList rows={6} />
          ) : (
            <List>
              {rows.map((tx) => (
                <TransactionRow
                  key={tx.id}
                  transaction={tx}
                  onSelect={() => navigate('transactionDetail', { id: tx.id })}
                />
              ))}
            </List>
          )}
        </section>
      </div>

      {confirmingDelete ? (
        <ConfirmDialog
          title={t('accountDetail.delete.confirm.title')}
          body={t('accountDetail.delete.confirm.body')}
          confirmLabel={t('cta.delete')}
          busy={busy}
          onConfirm={() => void remove()}
          onCancel={() => setConfirmingDelete(false)}
        />
      ) : null}
    </Screen>
  )
}
